import React, { useRef, useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import { savePDFForm, saveForm, updateForm } from './supabaseService';
import { useLocation } from 'react-router-dom';

const initialState = {
  description: '',
  wasteOrigin: '',
  ewcCode: '',
  wasteContained: {
    loose: false,
    bags: false,
    skip: false,
    drum: false,
    containers: false,
    other: false,
    otherDescription: '',
  },
  quantity: '',
  currentHolder: {
    name: '',
    address: '',
    postcode: '',
    sicCode: '',
    roles: {
      producer: false,
      holder: false,
      importer: false,
      registeredCarrier: false,
    },
    authorizationNumber: '',
  },
  receivingPerson: {
    name: '',
    address: '',
    roles: {
      holder: false,
      importer: false,
      registeredCarrier: false,
    },
    authorizationNumber: '',
  },
  placeOfTransfer: {
    address: '',
    date: '',
    time: '',
    brokerDetails: '',
  },
  signatures: {
    transferor: '',
    transferee: '',
  },
  preTreatment: {
    paper: false,
    card: false,
    food: false,
    compostable: false,
    glass: false,
  },
};

const styles = {
  container: {
    maxWidth: 900,
    margin: '40px auto',
    padding: 24,
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    width: '100%',
  },
  section: {
    marginBottom: 32,
    border: '1px solid #eee',
    borderRadius: 8,
    background: '#fafafa',
    padding: 16,
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: 20,
    marginBottom: 12,
    color: '#222',
  },
  label: {
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 6,
    display: 'block',
  },
  input: {
    width: '100%',
    padding: 8,
    fontSize: 16,
    border: '1px solid #ccc',
    borderRadius: 4,
    background: '#f9f9f9',
    marginBottom: 10,
    boxSizing: 'border-box',
  },
  checkboxRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 10,
    alignItems: 'center',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 15,
  },
  button: {
    padding: '14px 32px',
    background: '#ff0000',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontWeight: 'bold',
    fontSize: 18,
    cursor: 'pointer',
    marginTop: 24,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    maxWidth: 350,
  },
};

export default function WasteTransferForm() {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const pdfRef = useRef();
  const location = useLocation();

  // Load existing form data if editing
  useEffect(() => {
    if (location.state?.formData) {
      setForm(location.state.formData.data || initialState);
    }
  }, [location.state]);

  const LOGO_URL = '/logo.jpg';

  const handleInput = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };
  const handleRootInput = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const handleCheckbox = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };
  const handleNestedCheckbox = (section, role, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [role]: {
          ...prev[section][role],
          [key]: value,
        },
      },
    }));
  };

  const generatePDF = async () => {
    try {
      const element = pdfRef.current;
      
      // Generate PDF as blob
      const pdfBlob = await html2pdf()
        .set({
          margin: 0.5,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
        })
        .from(element)
        .outputPdf('blob');

      // Create filename with form name + date
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Waste_Transfer_Form_${currentDate}.pdf`;

      // Save to Firebase
      const formData = {
        formType: 'waste-transfer',
        title: 'Waste Transfer Form',
        location: form.wasteOrigin || 'Not specified',
        createdBy: 'Not specified',
        description: form.description || 'Not specified',
        wasteOrigin: form.wasteOrigin || 'Not specified',
        ewcCode: form.ewcCode || 'Not specified',
        wasteContained: form.wasteContained || {},
        quantity: form.quantity || 'Not specified',
        transferDate: currentDate
      };

                   await savePDFForm(formData, pdfBlob, fileName);
      
      // Also download locally for user convenience
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('PDF saved to library and downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const saveForm = async () => {
    try {
      setSaving(true);
      
      const formData = {
        formType: 'waste-transfer',
        title: `Waste Transfer - ${form.wasteOrigin || 'Unknown Origin'} - ${new Date().toLocaleDateString()}`,
        data: form,
        location: form.placeOfTransfer.address || 'Unknown Location',
        createdBy: form.currentHolder.name || 'Unknown User'
      };

      if (location.state?.formData?.id) {
        // Update existing form
        await updateForm(location.state.formData.id, formData);
      } else {
        // Save new form
        await saveForm(formData);
      }
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving form:', error);
      alert('Error saving form. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      <div ref={pdfRef}>
        <div style={{ textAlign: 'center', marginBottom: 10 }}>
          <img src={LOGO_URL} alt="Logo" style={{ width: 120, height: 'auto', marginBottom: 10 }} />
        </div>
        <h2 style={{ textAlign: 'center', marginBottom: 24 }}>Duty of Care: Controlled Waste Transfer Note</h2>
        {/* Section A */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Section A - Description of the Waste</div>
          <label style={styles.label}>Description</label>
          <input style={styles.input} value={form.description} onChange={e => handleRootInput('description', e.target.value)} placeholder="Description" />
          <label style={styles.label}>Waste Origin</label>
          <input style={styles.input} value={form.wasteOrigin} onChange={e => handleRootInput('wasteOrigin', e.target.value)} placeholder="Waste Origin" />
          <label style={styles.label}>EWC Code</label>
          <input style={styles.input} value={form.ewcCode} onChange={e => handleRootInput('ewcCode', e.target.value)} placeholder="EWC Code" />
          <div style={styles.checkboxRow}>
            <span style={styles.label}>Waste Contained:</span>
            {['loose','bags','skip','drum','containers','other'].map(type => (
              <label key={type} style={styles.checkboxLabel}>
                <input type="checkbox" checked={form.wasteContained[type]} onChange={e => handleCheckbox('wasteContained', type, e.target.checked)} />
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </label>
            ))}
            <input style={{...styles.input, width: 200, marginBottom: 0}} value={form.wasteContained.otherDescription} onChange={e => handleCheckbox('wasteContained', 'otherDescription', e.target.value)} placeholder="Other Description" />
          </div>
          <label style={styles.label}>Quantity</label>
          <input style={styles.input} value={form.quantity} onChange={e => handleRootInput('quantity', e.target.value)} placeholder="Quantity" />
        </div>
        {/* Section B */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Section B - Current Holder of the Waste (Transferor)</div>
          <label style={styles.label}>Name</label>
          <input style={styles.input} value={form.currentHolder.name} onChange={e => handleInput('currentHolder', 'name', e.target.value)} placeholder="Name" />
          <label style={styles.label}>Address</label>
          <input style={styles.input} value={form.currentHolder.address} onChange={e => handleInput('currentHolder', 'address', e.target.value)} placeholder="Address" />
          <label style={styles.label}>Postcode</label>
          <input style={styles.input} value={form.currentHolder.postcode} onChange={e => handleInput('currentHolder', 'postcode', e.target.value)} placeholder="Postcode" />
          <label style={styles.label}>SIC Code</label>
          <input style={styles.input} value={form.currentHolder.sicCode} onChange={e => handleInput('currentHolder', 'sicCode', e.target.value)} placeholder="SIC Code" />
          <div style={styles.checkboxRow}>
            <span style={styles.label}>Roles:</span>
            {['producer','holder','importer','registeredCarrier'].map(role => (
              <label key={role} style={styles.checkboxLabel}>
                <input type="checkbox" checked={form.currentHolder.roles[role]} onChange={e => handleNestedCheckbox('currentHolder', 'roles', role, e.target.checked)} />
                {role === 'registeredCarrier' ? 'Registered waste carrier' : role.charAt(0).toUpperCase() + role.slice(1).replace('holder','Holder of waste authorization')}
              </label>
            ))}
          </div>
          <label style={styles.label}>Authorization Number</label>
          <input style={styles.input} value={form.currentHolder.authorizationNumber} onChange={e => handleInput('currentHolder', 'authorizationNumber', e.target.value)} placeholder="Authorization Number" />
        </div>
        {/* Section C */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Section C - Person Receiving the Waste (Transferee)</div>
          <label style={styles.label}>Name</label>
          <input style={styles.input} value={form.receivingPerson.name} onChange={e => handleInput('receivingPerson', 'name', e.target.value)} placeholder="Name" />
          <label style={styles.label}>Address</label>
          <input style={styles.input} value={form.receivingPerson.address} onChange={e => handleInput('receivingPerson', 'address', e.target.value)} placeholder="Address" />
          <div style={styles.checkboxRow}>
            <span style={styles.label}>Roles:</span>
            {['holder','importer','registeredCarrier'].map(role => (
              <label key={role} style={styles.checkboxLabel}>
                <input type="checkbox" checked={form.receivingPerson.roles[role]} onChange={e => handleNestedCheckbox('receivingPerson', 'roles', role, e.target.checked)} />
                {role === 'registeredCarrier' ? 'Registered waste carrier' : role.charAt(0).toUpperCase() + role.slice(1).replace('holder','Holder of waste authorization')}
              </label>
            ))}
          </div>
          <label style={styles.label}>Authorization Number</label>
          <input style={styles.input} value={form.receivingPerson.authorizationNumber} onChange={e => handleInput('receivingPerson', 'authorizationNumber', e.target.value)} placeholder="Authorization Number" />
        </div>
        {/* Section E */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Section E - Place of Transfer</div>
          <label style={styles.label}>Address</label>
          <input style={styles.input} value={form.placeOfTransfer.address} onChange={e => handleInput('placeOfTransfer', 'address', e.target.value)} placeholder="Address" />
          <label style={styles.label}>Date</label>
          <input style={styles.input} value={form.placeOfTransfer.date} onChange={e => handleInput('placeOfTransfer', 'date', e.target.value)} placeholder="Date" />
          <label style={styles.label}>Time</label>
          <input style={styles.input} value={form.placeOfTransfer.time} onChange={e => handleInput('placeOfTransfer', 'time', e.target.value)} placeholder="Time" />
          <label style={styles.label}>Broker Details</label>
          <input style={styles.input} value={form.placeOfTransfer.brokerDetails} onChange={e => handleInput('placeOfTransfer', 'brokerDetails', e.target.value)} placeholder="Broker Details" />
        </div>
        {/* Section F */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Section F - Signatures</div>
          <label style={styles.label}>Transferor</label>
          <input style={styles.input} value={form.signatures.transferor} onChange={e => handleInput('signatures', 'transferor', e.target.value)} placeholder="Transferor Signature" />
          <label style={styles.label}>Transferee</label>
          <input style={styles.input} value={form.signatures.transferee} onChange={e => handleInput('signatures', 'transferee', e.target.value)} placeholder="Transferee Signature" />
        </div>
        {/* Section G */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Section G - Pre-Treatment Declaration</div>
          <div style={styles.checkboxRow}>
            {['paper','card','food','compostable','glass'].map(type => (
              <label key={type} style={styles.checkboxLabel}>
                <input type="checkbox" checked={form.preTreatment[type]} onChange={e => handleCheckbox('preTreatment', type, e.target.checked)} />
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </label>
            ))}
          </div>
        </div>
      </div>
      <button style={styles.button} onClick={generatePDF}>Download PDF</button>
      <button style={styles.button} onClick={saveForm} disabled={saving}>
        {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Form'}
      </button>
    </div>
  );
} 